import { getGridData } from 'Apis/library';
import { FilterCriteria } from '@ampsec/platform-client';
import { readLastUpdatedAtHint } from 'Core-utils/helpers/pageHints';
import { useMemo } from 'react';

interface ServerSideDataSourceProps {
  defaultFilter?: Record<string, any>;
  onRowCountChange?: (filtered: number, total: number) => void;
  onLastUpdatedAtChange?: (lastUpdatedAt?: string) => void;
}

const ATTRIBUTION_CONFLICT_LABEL = 'Conflict';
const ATTRIBUTION_NO_CONFLICT_LABEL = 'No Conflict';

function isUnassignedRow(row: any): boolean {
  return !!row?.name && String(row.name).toLowerCase() === 'unassigned';
}

function expandUnassignedRow(
  sourceRows: any[],
  filteredUserCount: number,
): { rowData: any[]; rowCount: number; sourceCount: number } {
  const unassignedIndex = sourceRows.findIndex(isUnassignedRow);
  if (unassignedIndex < 0) {
    return { rowData: sourceRows, rowCount: filteredUserCount, sourceCount: sourceRows.length };
  }

  const unassignedRow = sourceRows[unassignedIndex];
  const assets = Array.isArray(unassignedRow?.assets) ? unassignedRow.assets : [];
  const expandedRows = (assets.length > 0 ? assets : [undefined]).map((asset: any, idx: number) => {
    const assetId = asset?.id ? String(asset.id) : `asset-${idx}`;
    const rowId = `unassigned-${assetId}`;
    return {
      ...unassignedRow,
      id: rowId,
      __rowId: rowId,
      assets: asset ? [asset] : [],
    };
  });

  return {
    rowData: [...sourceRows.slice(0, unassignedIndex), ...expandedRows, ...sourceRows.slice(unassignedIndex + 1)],
    rowCount: filteredUserCount + expandedRows.length,
    sourceCount: sourceRows.length,
  };
}

function applyIdentitySliceFilter(filter: Record<string, any>, filterValues: Record<string, string>) {
  if (filterValues.activeProfile && filterValues.activeProfile !== 'all') {
    filter['identity.activeProfile'] = filterValues.activeProfile;
  }
  if (filterValues.recentActivity && filterValues.recentActivity !== 'all') {
    filter['identity.recentActivity'] = filterValues.recentActivity;
  }
  if (filterValues.mfaStatus && filterValues.mfaStatus !== 'all') {
    filter['identity.mfaStatus'] = filterValues.mfaStatus;
  }
}

function applyCollaborationSliceFilter(filter: Record<string, any>, filterValues: Record<string, string>) {
  if (filterValues.slackProfile && filterValues.slackProfile !== 'all') {
    filter['collaboration.slackProfile'] = filterValues.slackProfile;
  }
  if (filterValues.teamsProfile && filterValues.teamsProfile !== 'all') {
    filter['collaboration.teamsProfile'] = filterValues.teamsProfile;
  }
}

function applyAttributionStatusFilter(filter: Record<string, any>, attributionModel: any) {
  if (attributionModel?.filterType !== 'set' || !Array.isArray(attributionModel.values)) {
    return;
  }

  const values = attributionModel.values.filter((value: unknown): value is string => typeof value === 'string');
  const hasConflict = values.includes(ATTRIBUTION_CONFLICT_LABEL);
  const hasNoConflict = values.includes(ATTRIBUTION_NO_CONFLICT_LABEL);

  // When both values are selected, filtering is effectively disabled.
  if (hasConflict && hasNoConflict) {
    return;
  }

  if (hasConflict) {
    filter['attribution.status'] = 'NEGATIVE';
    return;
  }

  if (hasNoConflict) {
    filter['attribution.status'] = 'POSITIVE';
  }
}

export function ServerSideDataSource({ defaultFilter, onRowCountChange, onLastUpdatedAtChange }: ServerSideDataSourceProps) {
  const datasource = useMemo(
    () => ({
      getRows: (params: any) => {
        const { request, success, fail } = params;
        const { startRow, endRow, sortModel, filterModel } = request;
        const offset = startRow;
        const limit = endRow - startRow;

        const sort: Record<string, 'ASC' | 'DESC'> = {};
        if (Array.isArray(sortModel)) {
          sortModel.forEach((s: any) => {
            if (s.colId === 'department') {
              sort[s.colId] = s.sort && s.sort.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
            }
            if (s.colId === 'name') {
              sort.lastName = s.sort && s.sort.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
            }
            if (s.colId === 'attributionStatus') {
              sort.attributionStatus = s.sort && s.sort.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
            }
          });
        }

        const filter: Record<string, any> = {};
        if (filterModel) {
          const nameFilter = filterModel.name;
          if (
            nameFilter &&
            nameFilter.filterType === 'text' &&
            (nameFilter.type === 'contains' || nameFilter.type === 'equals') &&
            nameFilter.filter
          ) {
            filter.q = nameFilter.filter;
          }

          const deptFilter = filterModel.department;
          if (deptFilter?.filterType === 'departmentMultiSelect') {
            if (Array.isArray(deptFilter.values) && deptFilter.values.length > 0) {
              // Serialize as a JSON string so qs.stringify passes it as a single string param.
              // The backend's NAIVE_JSON_CHECK in controller.utils.ts detects /{.*}/ strings
              // and JSON.parses them back into an IdMatcher object before Zod validation.
              filter.department = JSON.stringify({ $in: deptFilter.values }) as any;
            }
          } else if (
            deptFilter &&
            deptFilter.filterType === 'text' &&
            (deptFilter.type === 'contains' || deptFilter.type === 'equals') &&
            deptFilter.filter
          ) {
            // Legacy text-filter fallback (kept for backwards compatibility)
            filter.department = deptFilter.filter;
          }

          const identityColumn = filterModel.identityAndAccess;
          if (identityColumn?.filterType === 'identityColumn' && identityColumn.filterValues) {
            applyIdentitySliceFilter(filter, identityColumn.filterValues);
          }

          // const legacyCombined = filterModel.identityCollabTraining;
          // if (legacyCombined?.filterType === 'identityCollabTraining' && legacyCombined.filterValues) {
          //   applyIdentitySliceFilter(filter, legacyCombined.filterValues);
          //   const fv = legacyCombined.filterValues;
          //   if (fv.slackProfile && fv.slackProfile !== 'all') {
          //     filter['collaboration.slackProfile'] = fv.slackProfile;
          //   }
          //   if (fv.trainingStatus && fv.trainingStatus !== 'all') {
          //     filter['training.trainingStatus'] = fv.trainingStatus;
          //   }
          // }

          const collaborationColumn = filterModel.collaboration;
          if (collaborationColumn?.filterType === 'collaborationColumn' && collaborationColumn.filterValues) {
            applyCollaborationSliceFilter(filter, collaborationColumn.filterValues);
          }

          const trainingColumn = filterModel.securityTraining;
          if (trainingColumn?.filterType === 'trainingColumn' && trainingColumn.filterValues?.trainingStatus) {
            const ts = trainingColumn.filterValues.trainingStatus;
            if (ts !== 'all') {
              filter['training.trainingStatus'] = ts;
            }
          }

          const userAsset = filterModel.numberOfEndpoints ?? filterModel.assets;
          if (userAsset && userAsset.filterType === 'userAsset') {
            if (userAsset.assetStatus && userAsset.assetStatus !== 'all') {
              if (userAsset.assetStatus === 'Assigned') {
                filter.assignmentFilter = 'assigned';
              } else if (userAsset.assetStatus === 'Unassigned') {
                filter.assignmentFilter = 'unassigned';
              }
            }
            if (userAsset.userAssetRelationship && userAsset.userAssetRelationship !== 'all') {
              filter.device = userAsset.userAssetRelationship;
            }
          }

          const evmCoverageFilter = filterModel.vulnerabilityManagement ?? filterModel.evmCoverage;
          if ((evmCoverageFilter?.filterType === 'vulnerabilityCoverage' || evmCoverageFilter?.filterType === 'evmCoverage') && evmCoverageFilter.value === 'missing') {
            filter['coverage.vulnerabilityManagement'] = 'missing';
          }

          const edrCoverageFilter = filterModel.endpointManagement;
          if (edrCoverageFilter?.filterType === 'endpointManagementCoverage' && edrCoverageFilter.value === 'missing') {
            filter['coverage.endpointManagement'] = 'missing';
          }

          applyAttributionStatusFilter(filter, filterModel.attributionStatus);
        }

        const apiParams = {
          offset,
          limit,
          ...defaultFilter,
          ...filter,
          sort,
        };

        getGridData('usersInsights', apiParams as FilterCriteria)
          .then((res) => {
            const filteredUserCount = res.hints?.filteredUserCount ?? 0;
            const sourceRows = Array.isArray(res.data) ? res.data : [];
            const { rowData, rowCount, sourceCount } = expandUnassignedRow(sourceRows, filteredUserCount);

            onLastUpdatedAtChange?.(readLastUpdatedAtHint(res.hints));
            onRowCountChange?.(filteredUserCount, rowCount);
            success({
              rowData,
              rowCount,
              sourceCount,
            });
          })
          .catch(() => {
            onLastUpdatedAtChange?.(undefined);
            fail();
          });
      },
    }),
    [defaultFilter, onRowCountChange, onLastUpdatedAtChange],
  );

  return { datasource };
}
